import { Question, QuestionCheckboxModel, QuestionCompositeModel, QuestionCustomModel, QuestionDropdownModel, QuestionFileModel, QuestionMatrixDropdownModel, QuestionMatrixDynamicModel, QuestionMatrixModel, QuestionPanelDynamicModel, QuestionRadiogroupModel, QuestionSelectBase } from "survey-core";
import { BaseColumn, CheckboxColumn, CommentColumn, CompositeQuestionColumn, CustomQuestionColumn, FileColumn, FlattenedCheckboxColumn, ImageColumn, MatrixColumn, MatrixDropdownColumn, MatrixDynamicColumn, OtherColumn, PanelDynamicColumn, SelectBaseColumn, SingleChoiceColumn } from "./columns";
import { IColumn, QuestionLocation } from "./config";
import { ITable } from "./table-interfaces";

export interface IColumnsBuilder {
  buildColumns(question: Question, table: ITable): Array<IColumn>;
}
export class DefaultColumnsBuilder<T extends Question = Question> implements IColumnsBuilder {
  protected createColumn(question: T, table: ITable) {
    return new BaseColumn(question, table);
  }

  protected buildColumnsCore(question: T, table: ITable): Array<IColumn> {
    const columns: Array<IColumn> = [];
    columns.push(this.createColumn(question, table));
    return columns;
  }

  public buildColumns(question: T, table: ITable): Array<IColumn> {
    const columns = this.buildColumnsCore(question, table);
    if(question.hasComment) {
      columns.push(new CommentColumn(question, table));
    }
    return columns;
  }
}

export class ColumnsBuilderFactory {
  public static Instance: ColumnsBuilderFactory = new ColumnsBuilderFactory();
  private constructor() {}

  private readonly columnsBuilders: {[index: string]: IColumnsBuilder } = {};
  private readonly defaultColumnsBuilder: IColumnsBuilder = new DefaultColumnsBuilder();

  registerBuilderColumn(type: string, columnsBuilder: IColumnsBuilder) {
    this.columnsBuilders[type] = columnsBuilder;
  }
  getColumnsBuilder(type: string) {
    return this.columnsBuilders[type] || this.defaultColumnsBuilder;
  }
}

export class SelectBaseColumnsBuilder<T extends QuestionSelectBase> extends DefaultColumnsBuilder<T> {
  public buildColumns(question: T, table: ITable): Array<IColumn> {
    const columns = super.buildColumns(question, table);
    if(question.hasOther && question.getStoreOthersAsComment()) {
      columns.push(new OtherColumn(question, table));
    }
    question.visibleChoices.forEach(choice => {
      if(Array.isArray(choice.elements)) {
        choice.elements.forEach((nestedQuestion: Question) => {
          const builder = ColumnsBuilderFactory.Instance.getColumnsBuilder(nestedQuestion.getType());
          const nestedColumns = builder.buildColumns(nestedQuestion, table);
          if(table.options.showChoiceNestedQuestionParentTitle !== false) {
            nestedColumns.forEach(col => col.addParentQuestion?.(question));
          }
          columns.push(...nestedColumns);
        });
      }
    });
    return columns;
  }
}
export class CheckboxColumnsBuilder extends SelectBaseColumnsBuilder<QuestionCheckboxModel> {
  protected createColumn(question: QuestionCheckboxModel, table: ITable): BaseColumn<QuestionCheckboxModel> {
    return new CheckboxColumn(question, table);
  }
  protected buildColumnsCore(question: QuestionCheckboxModel, table: ITable): Array<IColumn> {
    if(table.options.splitMultiSelectIntoColumns) {
      const columns: Array<IColumn> = [];
      question.visibleChoices.forEach(choice => {
        columns.push(new FlattenedCheckboxColumn(question, choice.value, choice.text, table));
      });
      return columns;
    }
    return super.buildColumnsCore(question, table);
  }
}
ColumnsBuilderFactory.Instance.registerBuilderColumn("checkbox", new CheckboxColumnsBuilder());
ColumnsBuilderFactory.Instance.registerBuilderColumn("tagbox", new CheckboxColumnsBuilder());
export class SingleChoiceColumnsBuilder extends SelectBaseColumnsBuilder<QuestionDropdownModel | QuestionRadiogroupModel> {
  protected createColumn(question: QuestionDropdownModel | QuestionRadiogroupModel, table: ITable): BaseColumn<QuestionDropdownModel | QuestionRadiogroupModel> {
    return new SingleChoiceColumn(question, table);
  }
}
ColumnsBuilderFactory.Instance.registerBuilderColumn("radiogroup", new SingleChoiceColumnsBuilder());
ColumnsBuilderFactory.Instance.registerBuilderColumn("dropdown", new SingleChoiceColumnsBuilder());

export class MatrixColumnsBuilder extends DefaultColumnsBuilder<QuestionMatrixModel> {
  protected buildColumnsCore(questionBase: Question, table: ITable): IColumn[] {
    const question = <QuestionMatrixModel>questionBase;
    const columns = [];
    question.rows.forEach(row => {
      columns.push(new MatrixColumn(question, row, table));
    });
    return columns;
  }
}
ColumnsBuilderFactory.Instance.registerBuilderColumn("matrix", new MatrixColumnsBuilder());

export class ImageColumnsBuilder extends DefaultColumnsBuilder {
  protected createColumn(question: Question, table: ITable): ImageColumn {
    return new ImageColumn(question, table);
  }
}
ColumnsBuilderFactory.Instance.registerBuilderColumn("signaturepad", new ImageColumnsBuilder());

export class FileColumnsBuilder extends DefaultColumnsBuilder<QuestionFileModel> {
  protected createColumn(question: QuestionFileModel, table: ITable): FileColumn {
    return new FileColumn(question, table);
  }
}
ColumnsBuilderFactory.Instance.registerBuilderColumn("file", new FileColumnsBuilder());

export class MatrixDropdownColumnBuilder extends DefaultColumnsBuilder {
  public buildColumns(questionBase: QuestionMatrixDropdownModel, table: ITable): Array<IColumn> {
    const question = <QuestionMatrixDropdownModel>questionBase;
    const detailRowThreshold = table.options.matrixDropdownDetailRowThreshold ?? 5;
    const columns = [];
    let columnIndex = 0;
    question.rows.forEach(row => {
      question.columns.forEach(col => {
        const column = new MatrixDropdownColumn(question, row, col, table);
        if(detailRowThreshold >= 0 && columnIndex >= detailRowThreshold) {
          column.location = QuestionLocation.Row;
        }
        columns.push(column);
        columnIndex++;
      });
    });
    return columns;
  }
}
ColumnsBuilderFactory.Instance.registerBuilderColumn("matrixdropdown", new MatrixDropdownColumnBuilder());

export class CustomColumnsBuilder extends DefaultColumnsBuilder<QuestionCustomModel> {
  protected createColumn(question: QuestionCustomModel, table: ITable): CustomQuestionColumn {
    return new CustomQuestionColumn(question, table);
  }
}
ColumnsBuilderFactory.Instance.registerBuilderColumn("custom", new CustomColumnsBuilder());

export class CompositeColumnsBuilder extends DefaultColumnsBuilder<QuestionCompositeModel> {
  public static ShowAsSeparateColumns = true;
  protected getDisplayName(question: QuestionCompositeModel, table: ITable): string {
    return table.useNamesAsTitles
      ? question.name
      : (question.locTitle?.renderedHtml || question.title || "").trim() || question.name;
  }
  protected buildColumnsCore(question: QuestionCompositeModel, table: ITable): Array<IColumn> {
    if(CompositeColumnsBuilder.ShowAsSeparateColumns) {
      const innerQuestions = [];
      question.contentPanel.addQuestionsToList(innerQuestions);
      let columns: Array<IColumn> = [];
      innerQuestions.forEach(innerQuestion => {
        const builder = ColumnsBuilderFactory.Instance.getColumnsBuilder(innerQuestion.getType());
        const cols = builder.buildColumns(innerQuestion, table);
        cols.forEach(col => {
          col.name = question.name + "." + col.name;
          col.displayName = this.getDisplayName(question, table) + " - " + this.getDisplayName(innerQuestion, table);
        });
        columns = columns.concat(cols);
      });
      return columns;
    }
    return super.buildColumnsCore(question, table);
  }
  protected createColumn(question: QuestionCompositeModel, table: ITable): CompositeQuestionColumn {
    return new CompositeQuestionColumn(question, table);
  }
}
ColumnsBuilderFactory.Instance.registerBuilderColumn("composite", new CompositeColumnsBuilder());

export class MatrixDynamicColumnsBuilder extends DefaultColumnsBuilder<QuestionMatrixDynamicModel> {
  protected createColumn(question: QuestionMatrixDynamicModel, table: ITable): MatrixDynamicColumn {
    return new MatrixDynamicColumn(question, table);
  }
}
ColumnsBuilderFactory.Instance.registerBuilderColumn("matrixdynamic", new MatrixDynamicColumnsBuilder());

export class PanelDynamicColumnsBuilder extends DefaultColumnsBuilder<QuestionPanelDynamicModel> {
  protected createColumn(question: QuestionPanelDynamicModel, table: ITable): PanelDynamicColumn {
    return new PanelDynamicColumn(question, table);
  }
}
ColumnsBuilderFactory.Instance.registerBuilderColumn("paneldynamic", new PanelDynamicColumnsBuilder());
